/**
 * Common utility functions shared across Individual, Third-Party,
 * and Opt-Out form scripts for SunPower Securities Settlement.
 */

import { unset } from "lodash";

// ---------------------------------------------------------------------------
// DOM Field Helpers
// ---------------------------------------------------------------------------

/**
 * Finds a field wrapper element by field name.
 * Looks for a div with data-field-id="_<fieldName>" first, then falls back
 * to locating the input/textarea/select by name and returning its parent.
 *
 * @param {string} fieldName - The name attribute of the field to find
 * @returns {HTMLElement} The field wrapper element
 */
export function findField(fieldName) {
    const container = document.querySelector(
        `div[data-field-id="_${fieldName}"]`
    );
    if (!container) {
        const input = document.querySelector(
            `input[name="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`
        );
        if (!input) {
            throw new Error(`Field "${fieldName}" not found`);
        }
        return input.type === "checkbox"
            ? input.parentElement.parentElement
            : input.parentElement;
    }
    return container;
}

/**
 * Moves a group of fields to a new location in the form.
 * Each field is inserted immediately after the reference node.
 *
 * @param {string[]} fieldNames - Array of field names to move
 * @param {Node} referenceNode - The node to insert after
 * @param {HTMLElement} container - The container element
 */
export function moveFields(fieldNames, referenceNode, container) {
    fieldNames.forEach((fieldName) => {
        const field = findField(fieldName);
        container.insertBefore(field, referenceNode.nextSibling);
    });
}

/**
 * Adds a CSS class to a list of fields.
 *
 * @param {string[]} fieldNames - Array of field names
 * @param {string} className - The CSS class to add
 */
export const addClassToFields = (fieldNames, className) => {
    fieldNames.forEach((fieldName) => {
        const field = findField(fieldName);
        field.classList.add(className);
    });
};

// ---------------------------------------------------------------------------
// Contact Section Organisation
// ---------------------------------------------------------------------------

/**
 * Organizes the contact section of the form by reordering field groups
 * relative to reference fields.
 */
export function organizeContactSection() {
    const fieldset = findField("last_name").parentElement;

    // Define field groups and their reference points
    const fieldGroups = [
        {
            fields: [
                "representative_or_custodian_name",
                "entity_name",
                "co_beneficial_owner_name",
                "beneficial_owner_name",
            ],
            referenceField: "last_name",
        },
        {
            fields: [
                "identification",
                "claimant_account_type_other_specify",
                "claimant_account_type",
                "account_number",
            ],
            referenceField: "confirm_email_address",
        },
    ];

    addClassToFields(fieldGroups[0].fields, "w-100");
    addClassToFields(fieldGroups[1].fields, "w-100");

    // Process each field group
    fieldGroups.forEach(({ fields, referenceField }) => {
        const referenceNode = findField(referenceField);
        moveFields(fields, referenceNode, fieldset);
    });
}

// ---------------------------------------------------------------------------
// Conditional Field Visibility
// ---------------------------------------------------------------------------

/**
 * Sets up conditional visibility for the "Other (please specify)" field.
 * The field is hidden by default and only shown when "other" is selected
 * in the claimant_account_type dropdown.
 */
export function setupClaimantAccountTypeConditional() {
    const otherSpecifyField = findField("claimant_account_type_other_specify");
    const accountTypeSelect = document.querySelector(
        'select[name="claimant_account_type"]'
    );

    if (!otherSpecifyField || !accountTypeSelect) return;

    // Hide the "Other (please specify)" field initially
    otherSpecifyField.style.display = "none";

    // Show/hide based on current value (in case of form re-init or edit)
    const toggleOtherField = () => {
        if (accountTypeSelect.value === "other") {
            otherSpecifyField.style.display = "";
        } else {
            otherSpecifyField.style.display = "none";
            // Clear the field value when hidden
            const input = otherSpecifyField.querySelector("input");
            if (input) input.value = "";
        }
    };

    // Check the initial state
    toggleOtherField();

    // Listen for changes
    accountTypeSelect.addEventListener("change", toggleOtherField);
}

// ---------------------------------------------------------------------------
// Template Content Helpers
// ---------------------------------------------------------------------------

/**
 * Adds the introduction content from the template to the target div.
 */
export function addIntroductionContent() {
    const template = document.getElementById("introduction-content-template");
    if (!template) {
        console.warn("Introduction content template not found");
        return;
    }

    // Find the target div by ID
    const targetDiv = document.getElementById("introduction_content");
    if (!targetDiv) {
        console.warn("Introduction content div not found");
        return;
    }

    // Clone the template content and append it to the target div
    const templateContent = template.content.cloneNode(true);
    targetDiv.appendChild(templateContent);
}

/**
 * Adds the signature legal content from the template to the target div.
 */
export function addSignatureLegalContent() {
    const template = document.getElementById(
        "signature-legal-content-template"
    );
    if (!template) {
        console.warn("Signature legal content template not found");
        return;
    }

    // Find the target div by ID
    const targetDiv = document.getElementById("signature-legal-description");
    if (!targetDiv) {
        console.warn("Signature legal description div not found");
        return;
    }

    // Clone the template content and append it to the target div
    const templateContent = template.content.cloneNode(true);
    targetDiv.appendChild(templateContent);
}

/**
 * Adds the general instructions content from the template to the target div.
 */
export function addGeneralInstructionsContent() {
    const template = document.getElementById(
        "general-instructions-content-template"
    );
    if (!template) {
        console.warn("General instructions content template not found");
        return;
    }

    // Find the target div by ID
    const targetDiv = document.getElementById("general_instructions_body");
    if (!targetDiv) {
        console.warn("General instructions body div not found");
        return;
    }

    // Clone the template content and append it to the target div
    const templateContent = template.content.cloneNode(true);
    targetDiv.appendChild(templateContent);
}

// ---------------------------------------------------------------------------
// Validation Helpers
// ---------------------------------------------------------------------------

/**
 * Disables email validation logic for email fields.
 * Used for paper claim submissions where email is not required.
 */
export function disableEmailValidation() {
    const emailElements = document.querySelectorAll('[data-validate*="email"]');
    if (emailElements.length === 0) return;
    emailElements.forEach((el) => {
        unset(el.dataset, "validate");
        el.required = undefined;
    });
}
